// Service worker registration + update flow + offline-queue drain.
//
// We register at /sw.js with scope /. On controllerchange (a new SW has
// taken over) we dispatch a window event 'sw:update-available' that the
// UI can listen to and surface a "tap to reload" toast.
//
// We also wire window.online to drain the offline mutation queue.
//
// Imported once from main.tsx as a side-effect module.

import { drainQueue, getQueuedCount } from './offlineQueue';

declare global {
  interface WindowEventMap {
    'sw:update-available': CustomEvent;
    'queue:drained': CustomEvent<{ ok: number; failed: number; dropped: number; deferred: number }>;
    'queue:enqueued': CustomEvent<{ count: number }>;
  }
}

function registerSW(): void {
  if (!('serviceWorker' in navigator)) return;
  // Don't register during local dev — Vite's HMR + a cache-first SW
  // fight each other. Only run on production builds (and when not
  // served from a `localhost` host).
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        // When a new SW is installed and another is controlling, show update.
        reg.addEventListener('updatefound', () => {
          const newSw = reg.installing;
          if (!newSw) return;
          newSw.addEventListener('statechange', () => {
            if (newSw.state === 'installed' && navigator.serviceWorker.controller) {
              window.dispatchEvent(new CustomEvent('sw:update-available'));
            }
          });
        });
        // Force an immediate check at boot. The browser's own update
        // schedule is unreliable on Android PWAs — without this nudge
        // a kept-warm app can miss new deploys for a day or more.
        void reg.update();
      })
      .catch((err) => {
        console.warn('SW registration failed:', err);
      });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // The new SW has taken control. Most UIs will reload after the
      // user accepts; we don't auto-reload here.
    });
  });
}

function tryUpdate(): void {
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.getRegistration().then((reg) => {
    void reg?.update();
  });
}

function wireOfflineQueue(): void {
  // Drain triggers, ordered loosest → tightest:
  //   - boot: unconditional, since `navigator.onLine` is unreliable on
  //     Android PWAs (can read true with no connectivity, or false long
  //     after connectivity returns). Failed fetches just stay queued —
  //     a drain attempt is cheap.
  //   - online: kept for the normal case.
  //   - visibilitychange: foregrounding the PWA is the most reliable
  //     signal on Android — `online` events often never fire after a
  //     suspend/resume but `visibilitychange` does.
  //   - queue:enqueued: gives a freshly-queued item one immediate retry
  //     in case the network just came back.
  //   - poll: 60s interval, only active while items are queued; clears
  //     itself once the queue empties.
  let pollHandle: number | null = null;

  function stopPoll(): void {
    if (pollHandle != null) {
      window.clearInterval(pollHandle);
      pollHandle = null;
    }
  }

  async function tryDrain(): Promise<void> {
    const initial = await getQueuedCount();
    if (initial === 0) {
      stopPoll();
      return;
    }
    const stats = await drainQueue();
    window.dispatchEvent(new CustomEvent('queue:drained', { detail: stats }));
    const remaining = await getQueuedCount();
    if (remaining > 0 && pollHandle == null) {
      pollHandle = window.setInterval(() => { void tryDrain(); }, 60_000);
    } else if (remaining === 0) {
      stopPoll();
    }
  }

  void tryDrain();

  window.addEventListener('online', () => { void tryDrain(); tryUpdate(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void tryDrain();
      // Also poke the SW to check for a new bundle. This is the single
      // most reliable update trigger on Android — the browser's own
      // schedule is too lazy for installed PWAs kept warm across days.
      tryUpdate();
    }
  });
  window.addEventListener('queue:enqueued', () => { void tryDrain(); });
}

export function initPwa(): void {
  registerSW();
  wireOfflineQueue();
}

export function applyUpdate(): void {
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg?.waiting) {
      reg.waiting.postMessage('SKIP_WAITING');
    }
    window.location.reload();
  });
}
